import { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Loader2, Eye, EyeOff, Shield, ArrowLeft, Lock, User, RefreshCw } from 'lucide-react';
import { api, invitationAPI, API_BASE, type InvitationConfig } from '../services/api';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: { id: string; email: string; name: string | null; credits: number; apiKey: string }) => void;
  defaultMode?: 'login' | 'register';
}

// 判断账号类型：手机号或邮箱
function detectAccountType(input: string): 'email' | 'phone' | 'unknown' {
  const trimmed = input.trim();
  // 如果包含 @ 符号，一定是邮箱
  if (trimmed.includes('@')) {
    return 'email';
  }
  // 纯11位数字且以1开头，认为是手机号
  if (/^1[3-9]\d{9}$/.test(trimmed)) {
    return 'phone';
  }
  // 其他情况无法确定
  return 'unknown';
}

export function AuthModal({ isOpen, onClose, onSuccess, defaultMode = 'login' }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'reset'>(defaultMode);
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [name, setName] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteConfig, setInviteConfig] = useState<InvitationConfig | null>(null);
  const [linuxdoEnabled, setLinuxdoEnabled] = useState(false);
  const [isOAuthRedirecting, setIsOAuthRedirecting] = useState(false);

  // 图形验证码
  const [captchaEnabled, setCaptchaEnabled] = useState(true);
  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  // 发码成功后隐藏图形验证码（已完成防轰炸使命，无需注册时再校验）
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    try {
      const data = await api.getCaptcha();
      setCaptchaId(data.captchaId);
      setCaptchaSvg(data.svg);
      setCaptchaText('');
    } catch {
      // 静默失败
    }
  }, []);

  // 获取验证码开关
  useEffect(() => {
    if (isOpen) {
      api.getCaptchaEnabled().then(setCaptchaEnabled).catch(() => setCaptchaEnabled(true));
    }
  }, [isOpen]);

  // 注册模式 / 手机号找回密码模式下加载验证码
  useEffect(() => {
    const isPhoneAccount = detectAccountType(account) === 'phone';
    const needCaptcha = isOpen && captchaEnabled && !captchaId &&
      (mode === 'register' || (mode === 'forgot' && isPhoneAccount));
    if (needCaptcha) {
      refreshCaptcha();
    }
  }, [isOpen, mode, captchaEnabled, captchaId, account, refreshCaptcha]);

  // 找回密码模式下，从邮箱切换到手机号时补加载验证码
  useEffect(() => {
    const isPhoneAccount = detectAccountType(account) === 'phone';
    if (isOpen && mode === 'forgot' && isPhoneAccount && captchaEnabled && !captchaId) {
      refreshCaptcha();
    }
  }, [isOpen, mode, account, captchaEnabled, captchaId, refreshCaptcha]);

  // 切换模式时清空验证码
  useEffect(() => {
    if (!isOpen) {
      setCaptchaId('');
      setCaptchaSvg('');
      setCaptchaText('');
      setCaptchaVerified(false);
    }
  }, [isOpen]);

  // 倒计时
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 获取邀请配置
  useEffect(() => {
    if (mode === 'register') {
      invitationAPI.getInvitationConfig().then(setInviteConfig).catch(() => {});
    }
  }, [mode]);

  // 获取 OAuth 配置状态
  useEffect(() => {
    if (isOpen) {
      api.getOAuthStatus().then((res) => {
        setLinuxdoEnabled(res.linuxdoEnabled);
      }).catch(() => {});
    }
  }, [isOpen]);

  // 从 URL 读取邀请码
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    if (invite) {
      setInviteCode(invite);
    }
  }, []);

  // 弹窗打开时根据 defaultMode 设置模式，并读取邀请码
  useEffect(() => {
    if (isOpen) {
      setMode(defaultMode);
      const params = new URLSearchParams(window.location.search);
      const invite = params.get('invite');
      if (invite) {
        setInviteCode(invite);
      }
    }
  }, [isOpen, defaultMode]);

  // 清空状态当关闭弹窗
  useEffect(() => {
    if (!isOpen) {
      setMode('login');
      setAccount('');
      setPassword('');
      setNewPassword('');
      setName('');
      setVerifyCode('');
      setError('');
      setSuccess('');
      setCountdown(0);
      setInviteCode('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const authMethod = detectAccountType(account);
  const isPhone = authMethod === 'phone';

  const handleSendVerifyCode = async (type: 'register' | 'reset-password' = 'register') => {
    if (!account) {
      setError('请输入邮箱或手机号');
      return;
    }

    if (authMethod === 'unknown') {
      setError('请输入正确的邮箱或手机号格式');
      return;
    }

    // 手机号方式
    if (isPhone) {
      if (!/^1[3-9]\d{9}$/.test(account)) {
        setError('手机号格式不正确');
        return;
      }

      // 图形验证码校验
      if (captchaEnabled && !captchaText) {
        setError('请输入图形验证码');
        return;
      }

      setIsSendingCode(true);
      setError('');

      try {
        const res = await fetch(`${API_BASE}/auth/send-sms-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: account, type, captchaId, captchaText }),
        });

        const data = await res.json();

        if (res.ok) {
          setCountdown(60);
          setError('');
          setSuccess('验证码已发送至您的手机');
          setTimeout(() => setSuccess(''), 3000);
          // 发码成功，隐藏图形验证码（已消费，注册时无需再校验）
          if (captchaEnabled) setCaptchaVerified(true);
        } else {
          setError(data.error || '发送失败');
          if (captchaEnabled) refreshCaptcha();
        }
      } catch (err) {
        setError('发送失败，请稍后重试');
        if (captchaEnabled) refreshCaptcha();
      } finally {
        setIsSendingCode(false);
      }
      return;
    }

    // 邮箱方式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(account)) {
      setError('邮箱格式不正确');
      return;
    }

    // 图形验证码校验（仅注册验证码需要，找回密码走 forgot-password 不需要）
    if (type === 'register' && captchaEnabled && !captchaText) {
      setError('请输入图形验证码');
      return;
    }

    setIsSendingCode(true);
    setError('');

    try {
      const endpoint = type === 'reset-password'
        ? `${API_BASE}/auth/forgot-password`
        : `${API_BASE}/auth/send-verify-code`;

      const body = type === 'reset-password'
        ? { email: account }
        : { email: account, type: 'register', captchaId, captchaText };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.ok) {
        setCountdown(60);
        setError('');
        setSuccess('验证码已发送至您的邮箱');
        setTimeout(() => setSuccess(''), 3000);
        // 发码成功，隐藏图形验证码（已消费，注册时无需再校验）
        if (type === 'register' && captchaEnabled) setCaptchaVerified(true);
      } else {
        setError(data.error || '发送失败');
        if (type === 'register' && captchaEnabled) refreshCaptcha();
      }
    } catch (err) {
      setError('发送失败，请稍后重试');
      if (type === 'register' && captchaEnabled) refreshCaptcha();
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (authMethod === 'unknown') {
      setError('请输入正确的邮箱或手机号格式');
      return;
    }

    setIsLoading(true);

    try {
      if (mode === 'register') {
        // 图形验证码已在"获取验证码"时校验过（captchaVerified），此处无需再校验
        try {
          const data = isPhone
            ? await api.registerPhone(account, password, name || undefined, verifyCode, inviteCode || undefined)
            : await api.register(account, password, name || undefined, verifyCode, inviteCode || undefined);
          onSuccess(data.user);
          onClose();
        } catch (err) {
          setError(err instanceof Error ? err.message : '注册失败');
        } finally {
          setIsLoading(false);
        }
        return;
      } else if (mode === 'login') {
        const data = isPhone
          ? await api.loginPhone(account, password)
          : await api.login(account, password);
        onSuccess(data.user);
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (authMethod === 'unknown') {
      setError('请输入正确的邮箱或手机号格式');
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = isPhone
        ? `${API_BASE}/auth/reset-password-phone`
        : `${API_BASE}/auth/reset-password`;

      const body = isPhone
        ? { phone: account, code: verifyCode, newPassword }
        : { email: account, code: verifyCode, newPassword };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('密码重置成功，请使用新密码登录');
        setTimeout(() => {
          setMode('login');
          setPassword('');
          setNewPassword('');
          setVerifyCode('');
          setSuccess('');
        }, 2000);
      } else {
        setError(data.error || '重置失败');
      }
    } catch (err) {
      setError('重置失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (newMode: 'login' | 'register' | 'forgot') => {
    setMode(newMode);
    setError('');
    setSuccess('');
    // 切换模式统一清空图形验证码，由 useEffect 按需重新加载
    setCaptchaId('');
    setCaptchaSvg('');
    setCaptchaText('');
    setCaptchaVerified(false);
    if (newMode === 'login') {
      setPassword('');
      setNewPassword('');
      setVerifyCode('');
    } else if (newMode === 'register') {
      setName('');
      setPassword('');
      setVerifyCode('');
      // 切换回注册模式时，重新从 URL 读取邀请码
      const params = new URLSearchParams(window.location.search);
      const invite = params.get('invite');
      if (invite) {
        setInviteCode(invite);
      }
    } else if (newMode === 'forgot') {
      setPassword('');
      setNewPassword('');
      setVerifyCode('');
    }
  };

  const handleLinuxDoLogin = async () => {
    setIsOAuthRedirecting(true);
    setError('');
    try {
      const { authUrl } = await api.getOAuthAuthorizeUrl(window.location.pathname + window.location.search);
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取授权链接失败');
      setIsOAuthRedirecting(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'login':
        return { title: '欢迎回来', subtitle: '登录继续创作' };
      case 'register':
        return { title: '创建账号', subtitle: '注册即送 2 积分' };
      case 'forgot':
        return { title: '找回密码', subtitle: '验证身份后重置密码' };
      default:
        return { title: '欢迎回来', subtitle: '登录继续创作' };
    }
  };

  const { title, subtitle } = getTitle();

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="absolute inset-0 bg-light-text/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white border border-light-border rounded-2xl w-full max-w-md mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-light-muted hover:text-light-text transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-accent-blue rounded-xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-light-text">{title}</h2>
              <p className="text-sm text-light-muted">{subtitle}</p>
            </div>
          </div>

          {mode === 'forgot' ? (
            // 找回密码表单
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">
                  账号 {account && authMethod !== 'unknown' && (
                    <span className="text-accent-blue font-normal">
                      ({isPhone ? '手机号' : '邮箱'})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
                  <input
                    type="text"
                    value={account}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      // 如果是邮箱（包含@），转为小写
                      setAccount(value.includes('@') ? value.toLowerCase() : value);
                    }}
                    placeholder="请输入邮箱或11位手机号"
                    required
                    className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                  />
                </div>
              </div>

              {/* 手机号找回密码需要图形验证码（后端 send-sms-code 校验） */}
              {isPhone && captchaEnabled && captchaSvg && !captchaVerified && (
                <div>
                  <label className="text-sm font-medium text-light-text mb-1.5 block">图形验证码</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={captchaText}
                      onChange={(e) => setCaptchaText(e.target.value)}
                      placeholder="输入图中字符"
                      maxLength={4}
                      required
                      className="flex-1 min-w-0 bg-white border border-light-border rounded-xl px-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                    />
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className="flex items-center gap-1 px-2 py-3 border border-light-border rounded-xl hover:border-accent-blue transition-colors bg-white overflow-hidden"
                      title="点击刷新"
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: captchaSvg }}
                        className="h-10 flex items-center justify-center overflow-hidden [&>svg]:w-full [&>svg]:h-[40px] [&>svg]:max-w-[110px]"
                        style={{ pointerEvents: 'none' }}
                      />
                      <RefreshCw className="w-4 h-4 text-light-muted shrink-0" />
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">验证码</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder="6位数字验证码"
                      maxLength={6}
                      required
                      className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSendVerifyCode('reset-password')}
                    disabled={isSendingCode || countdown > 0 || !account}
                    className="px-4 py-2 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap text-sm font-medium"
                  >
                    {isSendingCode ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : countdown > 0 ? (
                      `${countdown}s`
                    ) : (
                      '获取验证码'
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">新密码</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="至少 6 位"
                    required
                    minLength={6}
                    className="w-full bg-white border border-light-border rounded-xl pl-10 pr-12 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted hover:text-light-text transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-accent-blue text-white font-medium py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    处理中...
                  </>
                ) : (
                  '重置密码'
                )}
              </button>

              <button
                type="button"
                onClick={() => switchMode('login')}
                className="w-full flex items-center justify-center gap-2 text-sm text-light-muted hover:text-accent-blue transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                返回登录
              </button>
            </form>
          ) : (
            // 登录/注册表单
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">
                  账号 {account && authMethod !== 'unknown' && (
                    <span className="text-accent-blue font-normal">
                      ({isPhone ? '手机号' : '邮箱'})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
                  <input
                    type="text"
                    value={account}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      // 如果是邮箱（包含@），转为小写
                      setAccount(value.includes('@') ? value.toLowerCase() : value);
                    }}
                    placeholder="请输入邮箱或11位手机号"
                    required
                    className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                  />
                </div>
              </div>

              {/* 注册时显示图形验证码（在短信验证码之前，先输图形码再点获取短信） */}
              {mode === 'register' && captchaEnabled && captchaSvg && !captchaVerified && (
                <div>
                  <label className="text-sm font-medium text-light-text mb-1.5 block">图形验证码</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={captchaText}
                      onChange={(e) => setCaptchaText(e.target.value)}
                      placeholder="输入图中字符"
                      maxLength={4}
                      required
                      className="flex-1 min-w-0 bg-white border border-light-border rounded-xl px-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                    />
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className="flex items-center gap-1 px-2 py-3 border border-light-border rounded-xl hover:border-accent-blue transition-colors bg-white overflow-hidden"
                      title="点击刷新"
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: captchaSvg }}
                        className="h-10 flex items-center justify-center overflow-hidden [&>svg]:w-full [&>svg]:h-[40px] [&>svg]:max-w-[110px]"
                        style={{ pointerEvents: 'none' }}
                      />
                      <RefreshCw className="w-4 h-4 text-light-muted shrink-0" />
                    </button>
                  </div>
                </div>
              )}

              {/* 注册时显示验证码 */}
              {mode === 'register' && (
                <div>
                  <label className="text-sm font-medium text-light-text mb-1.5 block">验证码</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
                      <input
                        type="text"
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value)}
                        placeholder="6位数字验证码"
                        maxLength={6}
                        required
                        className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSendVerifyCode('register')}
                      disabled={isSendingCode || countdown > 0 || !account}
                      className="px-4 py-2 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap text-sm font-medium"
                    >
                      {isSendingCode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : countdown > 0 ? (
                        `${countdown}s`
                      ) : (
                        '获取验证码'
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-light-muted mt-1.5">
                    验证码将发送至您的{isPhone ? '手机' : '邮箱'}，{isPhone ? '3' : '10'}分钟内有效
                  </p>
                </div>
              )}

              {/* 邀请码 - 隐藏传递 + 有效性提示 */}
              {mode === 'register' && inviteCode && inviteConfig?.inviteEnabled && !(inviteConfig.invitePhoneOnly && !isPhone) && (
                <>
                  <input
                    type="hidden"
                    value={inviteCode}
                  />
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <p className="text-sm text-green-600">
                      您正在使用邀请码注册，额外获得 {inviteConfig.inviteeCredits} 积分奖励
                    </p>
                  </div>
                </>
              )}
              {mode === 'register' && inviteCode && inviteConfig?.inviteEnabled && inviteConfig.invitePhoneOnly && !isPhone && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-sm text-amber-600">
                    当前为邮箱注册，无法享受邀请奖励。切换至手机号注册即可获得 {inviteConfig.inviteeCredits} 积分奖励
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">密码</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="至少 6 位"
                    required
                    minLength={6}
                    className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted hover:text-light-text transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* 登录时显示忘记密码 */}
              {mode === 'login' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-sm text-accent-blue hover:underline transition-colors"
                  >
                    忘记密码？
                  </button>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-accent-blue text-white font-medium py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    处理中...
                  </>
                ) : (
                  mode === 'login' ? '登录' : '注册'
                )}
              </button>

              {mode === 'login' && linuxdoEnabled && (
                <>
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-light-border"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 bg-white text-light-muted">或</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleLinuxDoLogin}
                    disabled={isOAuthRedirecting}
                    className="w-full border border-light-border text-light-text font-medium py-3 rounded-xl hover:border-accent-blue hover:text-accent-blue hover:bg-accent-blue/5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isOAuthRedirecting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        跳转中...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="2" width="20" height="20" rx="6" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M7 12.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        使用 Linux Do 登录
                      </>
                    )}
                  </button>
                </>
              )}
            </form>
          )}

          {mode !== 'forgot' && (
            <div className="mt-6 text-center">
              <button
                onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                className="text-sm text-light-muted hover:text-accent-blue transition-colors font-medium"
              >
                {mode === 'login' ? '没有账号？立即注册' : '已有账号？立即登录'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
